import type { CSSProperties, KeyboardEvent, ReactNode } from 'react';
import { Icon, type IconName } from './Icon.js';

export function Badge({ tone = 'muted', children }: {
  tone?: 'ok' | 'warn' | 'danger' | 'info' | 'muted';
  children: ReactNode;
}) {
  return <span className={`arc-badge arc-badge-${tone}`}>{children}</span>;
}

/**
 * Status is never carried by colour alone — the dot plus the text label do the
 * work, so it survives greyscale and colour blindness (doc 07 §5).
 */
export function StatusBadge({ status }: { status: string }) {
  const map: Record<string, 'ok' | 'warn' | 'danger' | 'info' | 'muted'> = {
    active: 'ok', registered: 'ok', ready: 'ok', up: 'ok', published: 'ok',
    provisioning: 'info', pending: 'info', draining: 'warn', unknown: 'muted',
    suspended: 'warn', disabled: 'muted', closed: 'muted', draft: 'warn',
    failed: 'danger', unregistered: 'danger', down: 'danger', degraded: 'warn',
  };
  return (
    <Badge tone={map[status] ?? 'muted'}>
      <span className="arc-dot" aria-hidden="true" />
      {status}
    </Badge>
  );
}

export function Empty({ icon = 'search', title, hint, action }: {
  icon?: IconName; title: string; hint?: string; action?: ReactNode;
}) {
  return (
    <div className="arc-empty">
      <div className="arc-empty-icon"><Icon name={icon} size={17} /></div>
      <div className="arc-empty-title">{title}</div>
      {hint && <div style={{ fontSize: 12.5, maxWidth: 420, margin: '0 auto 12px' }}>{hint}</div>}
      {action}
    </div>
  );
}

export function TableSkeleton({ rows = 6, cols = 5 }: { rows?: number; cols?: number }) {
  return (
    <tbody>
      {Array.from({ length: rows }).map((_, r) => (
        <tr key={r}>
          {Array.from({ length: cols }).map((__, c) => (
            <td key={c}>
              <div className="arc-skeleton" style={{ height: 11, width: c === 0 ? '58%' : '76%' }} />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );
}

/**
 * One figure.
 *
 * `icon`/`hue` are decoration and are treated as such: the tile is identified
 * by its label, and `tone` -- which does carry meaning -- colours the NUMBER,
 * never the icon. Keeping those two channels apart is what stops a red icon
 * chosen for variety reading as an alarm.
 *
 * `bare` drops the card so the tile can sit inside a shared one; see StatGroup.
 */
export function Stat({ label, value, sub, tone, icon, hue, bare }: {
  label: string; value: ReactNode; sub?: ReactNode | undefined;
  tone?: 'ok' | 'warn' | 'danger' | undefined;
  icon?: IconName | undefined;
  hue?: 1 | 2 | 3 | 4 | 5 | undefined;
  bare?: boolean | undefined;
}) {
  return (
    <div className={bare ? 'arc-stat' : 'arc-card arc-stat'}>
      <div className="arc-stat-body">
        <div className="arc-stat-label">{label}</div>
        <div className="arc-stat-value" style={tone ? { color: `var(--arc-${tone})` } : undefined}>
          {value}
        </div>
        {sub !== undefined && <div className="arc-stat-sub">{sub}</div>}
      </div>
      {icon && (
        <div className="arc-stat-icon" data-hue={hue ?? 1}>
          <Icon name={icon} size={17} />
        </div>
      )}
    </div>
  );
}

/**
 * Several figures in one card, divided by hairlines.
 *
 * Four separate cards say "four things"; one card says "one summary", and a
 * summary is what a row of related totals is.
 */
export function StatGroup({ children }: { children: ReactNode }) {
  return <div className="arc-card arc-stat-group">{children}</div>;
}

/**
 * The head of a section inside a tab: a title, at most one line under it, and
 * the section's action on the right. Every tab wrote this by hand, each with
 * its own margins; one component keeps them the same.
 */
export function SectionHead({ title, sub, action }: {
  title: ReactNode; sub?: ReactNode; action?: ReactNode;
}) {
  return (
    <div className="arc-row" style={{ marginBottom: 14 }}>
      <div>
        <h2 className="arc-h2">{title}</h2>
        {sub !== undefined && <p className="arc-muted" style={{ fontSize: 12.5, marginTop: 2 }}>{sub}</p>}
      </div>
      <div className="arc-spacer" />
      {action}
    </div>
  );
}

/**
 * A small set of mutually exclusive choices -- a period, a view -- as a row of
 * buttons. For a choice between PURPOSES, where each option needs a sentence,
 * use the arc-kind cards instead.
 */
export function Segmented<T extends string | number>({ options, value, onChange, label }: {
  /**
   * `[value, text]`, or `[value, text, count]` when how many each choice holds
   * helps somebody pick it -- the options of a filter, say.
   */
  options: ReadonlyArray<readonly [T, string, number?]>;
  value: T;
  onChange: (v: T) => void;
  /** What is being chosen, for assistive technology. */
  label: string;
}) {
  return (
    // Wraps rather than running off the edge: a filter with counts is wider
    // than a period picker, and a hidden choice is a choice nobody makes.
    <div className="arc-row" style={{ gap: 4, flexWrap: 'wrap' }} role="group" aria-label={label}>
      {options.map(([v, text, count]) => (
        <button key={String(v)} type="button" aria-pressed={value === v}
          className={`arc-btn arc-btn-sm ${value === v ? 'arc-btn-primary' : 'arc-btn-ghost'}`}
          onClick={() => onChange(v)}>
          {text}
          {count !== undefined && <span className="arc-seg-count">{count}</span>}
        </button>
      ))}
    </div>
  );
}

export function ErrorBanner({ error }: { error: unknown }) {
  const message = error instanceof Error ? error.message : 'Something went wrong';
  const requestId =
    typeof error === 'object' && error !== null && 'problem' in error
      ? (error as { problem?: { request_id?: string } }).problem?.request_id
      : undefined;
  return (
    <div className="arc-banner arc-banner-danger" style={{ marginBottom: 14 }} role="alert">
      <Icon name="warning" size={14} />
      <div>
        <div style={{ fontWeight: 600 }}>{message}</div>
        {/* Quoting this to support resolves straight to a trace (doc 05 §3). */}
        {requestId && (
          <div className="arc-mono" style={{ opacity: .75, marginTop: 2 }}>{requestId}</div>
        )}
      </div>
    </div>
  );
}

export function SearchInput({ value, onChange, placeholder, label }: {
  value: string; onChange: (v: string) => void; placeholder?: string; label: string;
}) {
  return (
    <div className="arc-search">
      <Icon name="search" size={13} />
      <input
        className="arc-input" value={value} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)} aria-label={label}
      />
    </div>
  );
}

/**
 * A tab strip.
 *
 * This exists because `role="tab"` was hand-written at seven call sites across
 * four files and only ONE of them carried `aria-selected`. The role is not
 * decoration: a screen reader announcing "tab" and nothing else leaves the user
 * with no way to tell which of nine tabs they are on. `data-active` styled the
 * strip correctly the whole time, so it looked right and read wrong, and no
 * amount of looking at it would have shown that.
 *
 * `role="tablist"` also PROMISES arrow-key navigation -- a user who lands on
 * the strip expects Left/Right to move between tabs and Home/End to jump to the
 * ends. That promise was unkept everywhere too, so it is kept here once.
 */
export function Tabs({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
    if (!keys.includes(e.key)) return;
    const tabs = [...e.currentTarget.querySelectorAll<HTMLButtonElement>('[role="tab"]')]
      .filter((t) => !t.disabled);
    if (tabs.length === 0) return;
    const at = tabs.indexOf(document.activeElement as HTMLButtonElement);
    const next = e.key === 'Home' ? 0
      : e.key === 'End' ? tabs.length - 1
      // Wraps, which is what a tablist does: the strip is a ring, not a line.
      : at < 0 ? 0
      : (at + (e.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
    e.preventDefault();
    tabs[next]?.focus();
    tabs[next]?.click();
  };
  return (
    <div className="arc-tabs" role="tablist" onKeyDown={onKeyDown}
      {...(style ? { style } : {})}>
      {children}
    </div>
  );
}

export function Tab({ active, onSelect, children }: {
  active: boolean; onSelect: () => void; children: ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      /* Only the selected tab is in the tab order. Tabbing through a nine-tab
         strip to reach the content below it is why roving tabindex exists. */
      tabIndex={active ? 0 : -1}
      className="arc-tab"
      data-active={active}
      onClick={onSelect}
    >
      {children}
    </button>
  );
}
